export interface EducationPlanItem {
  id: string;
  /** INTERNAL | EXTERNAL */
  educationType: string;
  /** 교육 과정명 */
  courseName: string;
  /** 세부 교육 내용 */
  detailContent: string;
  target: string;
  period: string;
  hours: string;
  method: string;
  owner: string;
}

function newEducationPlanItemId() {
  return `i-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}`;
}

export function createEmptyEducationPlanItem(): EducationPlanItem {
  return {
    id: newEducationPlanItemId(),
    educationType: 'INTERNAL',
    courseName: '',
    detailContent: '',
    target: '',
    period: '',
    hours: '',
    method: '',
    owner: '',
  };
}

export function educationPlanTypeLabel(type?: string | null): string {
  const t = (type || '').toUpperCase();
  if (t === 'EXTERNAL') return '사외';
  if (t === 'INTERNAL') return '사내';
  return type?.trim() || '';
}

export function parseEducationPlanItems(json?: string | null): EducationPlanItem[] {
  if (!json?.trim()) return [createEmptyEducationPlanItem()];
  try {
    const arr = JSON.parse(json);
    if (!Array.isArray(arr) || !arr.length) return [createEmptyEducationPlanItem()];
    return arr.map(
      (
        raw: Partial<EducationPlanItem> & {
          type?: string;
          topic?: string;
          content?: string;
        },
      ) => {
        const typeRaw = String(raw.educationType || raw.type || 'INTERNAL').toUpperCase();
        return {
          id: String(raw.id || newEducationPlanItemId()),
          educationType: typeRaw === 'EXTERNAL' ? 'EXTERNAL' : 'INTERNAL',
          courseName: String(raw.courseName || raw.topic || ''),
          detailContent: String(raw.detailContent || raw.content || ''),
          target: String(raw.target || ''),
          period: String(raw.period || ''),
          hours: String(raw.hours || ''),
          method: String(raw.method || ''),
          owner: String(raw.owner || ''),
        };
      },
    );
  } catch {
    return [createEmptyEducationPlanItem()];
  }
}

export function countEducationPlanItems(json?: string | null): number {
  return parseEducationPlanItems(json).filter(
    (x) => x.courseName.trim() || x.detailContent.trim() || x.target.trim(),
  ).length;
}

/** 교육 기록 참석자 (서명 포함 JSON) */
export interface EducationAttendee {
  id: string;
  name: string;
  dept: string;
  /** 조직 멤버 매핑 (선택) */
  userId?: number | null;
  departmentId?: number | null;
  /** true면 조직 선택이 아닌 직접 입력 */
  manualEntry?: boolean;
  signatureData?: string;
}

export interface EducationAnnualPlanAttachment {
  attachmentId: number;
  fileName: string;
  fileExt?: string | null;
  fileSize?: number | null;
}

export interface EducationAnnualPlan {
  planId: number;
  planYear: number;
  planNo?: string | null;
  title?: string | null;
  status?: string | null;
  preparedBy?: string | null;
  approvedBy?: string | null;
  authorUserId?: number | null;
  authorName?: string | null;
  reviewerName?: string | null;
  approverName?: string | null;
  approverUserId?: number | null;
  approvalInProgress?: boolean | null;
  pendingApprovalRequestId?: number | null;
  canCancelApproval?: boolean | null;
  canRevoke?: boolean | null;
  canConfirm?: boolean | null;
  canReopen?: boolean | null;
  itemsJson?: string | null;
  remark?: string | null;
  attachmentCount?: number | null;
  attachments?: EducationAnnualPlanAttachment[] | null;
  createdAt?: string | null;
  updatedAt?: string | null;
}

export interface EducationAnnualPlanPayload {
  planYear?: number;
  title?: string;
  status?: string;
  preparedBy?: string;
  approvedBy?: string;
  itemsJson?: string;
  remark?: string;
}

export interface EducationRecordAttachment {
  attachmentId: number;
  fileName: string;
  fileExt?: string | null;
  fileSize?: number | null;
}

export interface EducationRecord {
  recordId: number;
  recordNo?: string | null;
  educationDate?: string | null;
  startTime?: string | null;
  endTime?: string | null;
  title?: string | null;
  educationType?: string | null;
  instructor?: string | null;
  place?: string | null;
  hours?: string | null;
  attendees?: string | null;
  content?: string | null;
  resultText?: string | null;
  status?: string | null;
  authorUserId?: number | null;
  authorName?: string | null;
  reviewerName?: string | null;
  approverName?: string | null;
  approverUserId?: number | null;
  approvalInProgress?: boolean | null;
  pendingApprovalRequestId?: number | null;
  canCancelApproval?: boolean | null;
  canRevoke?: boolean | null;
  canConfirm?: boolean | null;
  canReopen?: boolean | null;
  remark?: string | null;
  attachmentCount?: number | null;
  attachments?: EducationRecordAttachment[] | null;
}

export interface EducationRecordPayload {
  recordNo?: string;
  educationDate?: string;
  startTime?: string;
  endTime?: string;
  title: string;
  educationType?: string;
  instructor?: string;
  place?: string;
  hours?: string;
  attendees?: string;
  content?: string;
  resultText?: string;
  status?: string;
  remark?: string;
}

export interface ExternalEducationReportAttachment {
  attachmentId: number;
  fileName: string;
  fileExt?: string | null;
  fileSize?: number | null;
}

export interface ExternalEducationReport {
  reportId: number;
  reportNo?: string | null;
  courseName?: string | null;
  organizer?: string | null;
  periodFrom?: string | null;
  periodTo?: string | null;
  traineeName?: string | null;
  department?: string | null;
  hours?: string | null;
  costText?: string | null;
  content?: string | null;
  applicationPlan?: string | null;
  status?: string | null;
  authorUserId?: number | null;
  authorName?: string | null;
  reviewerName?: string | null;
  approverName?: string | null;
  approverUserId?: number | null;
  approvalInProgress?: boolean | null;
  pendingApprovalRequestId?: number | null;
  canCancelApproval?: boolean | null;
  canRevoke?: boolean | null;
  canConfirm?: boolean | null;
  canReopen?: boolean | null;
  remark?: string | null;
  attachmentCount?: number | null;
  attachments?: ExternalEducationReportAttachment[] | null;
}

export interface ExternalEducationReportPayload {
  reportNo?: string;
  courseName: string;
  organizer?: string;
  periodFrom?: string;
  periodTo?: string;
  traineeName?: string;
  department?: string;
  hours?: string;
  costText?: string;
  content?: string;
  applicationPlan?: string;
  status?: string;
  remark?: string;
}

function newAttendeeId() {
  return `a-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`;
}

export function createEmptyEducationAttendee(): EducationAttendee {
  return {
    id: newAttendeeId(),
    name: '',
    dept: '',
    userId: null,
    departmentId: null,
    manualEntry: false,
    signatureData: '',
  };
}

export function defaultEducationAttendees(): EducationAttendee[] {
  return [createEmptyEducationAttendee()];
}

/** 구형 텍스트(쉼표/줄바꿈) 또는 JSON 배열 → 참석자 목록 */
export function parseEducationAttendees(raw?: string | null): EducationAttendee[] {
  if (!raw?.trim()) return defaultEducationAttendees();
  const text = raw.trim();
  if (text.startsWith('[')) {
    try {
      const parsed = JSON.parse(text) as Partial<EducationAttendee>[];
      if (Array.isArray(parsed) && parsed.length > 0) {
        return parsed.map((row) => ({
          id: row.id || newAttendeeId(),
          name: String(row.name || '').trim(),
          dept: String(row.dept || '').trim(),
          userId: row.userId != null ? Number(row.userId) : null,
          departmentId: row.departmentId != null ? Number(row.departmentId) : null,
          manualEntry: Boolean(row.manualEntry) || (row.userId == null && Boolean(row.name)),
          signatureData: row.signatureData || '',
        }));
      }
    } catch {
      // fall through to legacy text
    }
  }
  const names = text
    .split(/[\n,;/]+/)
    .map((s) => s.trim())
    .filter(Boolean);
  if (!names.length) return defaultEducationAttendees();
  return names.map((name) => ({
    id: newAttendeeId(),
    name,
    dept: '',
    userId: null,
    departmentId: null,
    manualEntry: true,
    signatureData: '',
  }));
}

export function serializeEducationAttendees(rows: EducationAttendee[]): string {
  const cleaned = rows
    .map((r) => ({
      id: r.id || newAttendeeId(),
      name: (r.name || '').trim(),
      dept: (r.dept || '').trim(),
      userId: r.manualEntry ? null : r.userId ?? null,
      departmentId: r.manualEntry ? null : r.departmentId ?? null,
      manualEntry: Boolean(r.manualEntry) || (r.userId == null && Boolean((r.name || '').trim())),
      signatureData: r.signatureData || '',
    }))
    .filter((r) => r.name || r.signatureData || r.userId);
  return JSON.stringify(cleaned.length ? cleaned : []);
}

export function educationAttendeesSummary(raw?: string | null): string {
  const rows = parseEducationAttendees(raw).filter((r) => r.name);
  if (!rows.length) return '-';
  const names = rows.map((r) => r.name);
  if (names.length <= 3) return names.join(', ');
  return `${names.slice(0, 3).join(', ')} 외 ${names.length - 3}명`;
}

